import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import {OEGroupControl, OEControl, OEIconButtonGroupControl} from '../../elements/controls/oe-controls';
import OEIconCodes from '../../../lib/oe-icon-codes';
import OEPopover from '../../views/oe-popover';
import retardUpdate from '../../../lib/react/update-retarder';
import {OEArrowDataFlags, OEArrowModels, OEArrowExtControllerBtnIds, OEArrowDataSlots} from './oe-arrow-model';
import OEObjectModelValueEdit from '../../elements/object-model-value-edit/oe-object-model-value-edit';

export class OEArrowExtController extends React.PureComponent {

    constructor(props)  {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.id = typeof(this.props.id) === 'number' ? this.props.id : null;

        this.data = null;

        this.state = this.defaultState();
        
        this.updateLanguage = this.updateLanguage.bind(this);
        this.onArrowChanged = this.onArrowChanged.bind(this);
        this.onArrowDataSlotChanged = this.onArrowDataSlotChanged.bind(this);
        this.onUIControllerStateChanged = this.onUIControllerStateChanged.bind(this);

        this.onStyleChange = this.onStyleChange.bind(this);
        this.onTransformationChange = this.onTransformationChange.bind(this);
        this.onDataSlotButtonPressed = this.onDataSlotButtonPressed.bind(this);
    }

    defaultState()  {
        return {
            uiEnabled: false,
            data: this.data,
            reducedStyle: null,
            strings: {
                styleLabel: '',
                transformationLabel: ''
            },
            dataSlots: {
                style: false,
                transformation: false
            }
        };
    }

    componentWillReceiveProps(nextProps) {
        if(nextProps.id !== this.props.id)     {
            this.id = typeof(nextProps.id) === 'number' ? nextProps.id : null;
            this.updateArrowData();
        }
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.arrowChanged, this.onArrowChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.arrowDataSlotChanged, this.onArrowDataSlotChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.arrowChanged, this.onArrowChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.arrowDataSlotChanged, this.onArrowDataSlotChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);	
    }

    updateLanguage()   {
        this.setState({
            strings: {
                styleLabel: this.oe.sharedInterface.getLocalizedStringEnc('arrow_ext_view_style'),
                transformationLabel: this.oe.sharedInterface.getLocalizedStringEnc('arrow_ext_view_transformation')
            }
        });
    }

    updateArrowData()  {
        if(!this.oe.isReady() || this.id === null)    return;

        this.data = this.oe.sharedInterface.getUIControllerArrow().getArrowData(this.id);
        
        let reducedStyle = {
            width: this.data.style.width,
            length: this.data.style.length,
        };

        this.setState({data: clone(this.data), reducedStyle: reducedStyle});
    }

    updateDataSlots()  {
        if(!this.oe.isReady())  return;
        let controller = this.oe.sharedInterface.getUIControllerArrow();

        this.setState({dataSlots: {
            style: controller.hasArrowDataSlot(OEArrowDataSlots.style),
            transformation: controller.hasArrowDataSlot(OEArrowDataSlots.transformation)
        }});
    }

    updateUIState()    {
        this.setState({
            uiEnabled: this.oe.sharedInterface.getUIControllerArrow().getUIEnabled()
        });
    }

    updateState(released)   {
        if(!this.oe.isReady() || released)   {
            this.data = null;
            this.setState(this.defaultState());
            return;
        }

        retardUpdate(this, () => {
            this.updateLanguage();
            this.updateUIState();
            this.updateArrowData();
            this.updateDataSlots();
        });
    }

    onArrowChanged(message, userInfo)   {
        if(userInfo.ID !== this.id)  return;
        this.updateArrowData();
    }

    onArrowDataSlotChanged(message, userInfo)   {
        if(![OEArrowDataSlots.style, OEArrowDataSlots.transformation].includes(userInfo.slot)) return;
        this.updateDataSlots();
    }

    onUIControllerStateChanged(message, userInfo)    {
        if(userInfo.type === this.oe.Module.UIControllerType.arrow) {
            this.updateUIState();
        }
    }

    render()    {
        return (
            <React.Fragment>

                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                
                <div className={'arrow-ext-controller ' + this.props.className}>

                    <OEGroupControl title={this.state.strings.styleLabel}>
                        
                        <OEIconButtonGroupControl
                            buttons={[
                                {
                                    id: OEArrowExtControllerBtnIds.copyStyle,
                                    icon: OEIconCodes.arrowCopyStyle
                                },
                                {
                                    id: OEArrowExtControllerBtnIds.pasteStyle,
                                    disabled: !this.state.dataSlots.style,
                                    icon: OEIconCodes.arrowPasteStyle
                                }
                            ]}
                            onPressed={this.onDataSlotButtonPressed}
                        />

                        <OEControl>
                            <OEObjectModelValueEdit
                                className="multi-column"
                                model={OEArrowModels.style}
                                object={this.state.reducedStyle}
                                onChange={this.onStyleChange}
                            />
                        </OEControl>

                    </OEGroupControl>

                    <OEGroupControl title={this.state.strings.transformationLabel}>

                        <OEIconButtonGroupControl
                            buttons={[
                                {
                                    id: OEArrowExtControllerBtnIds.copyTransformation,
                                    icon: OEIconCodes.arrowCopyTransformation
                                },
                                {
                                    id: OEArrowExtControllerBtnIds.pasteTransformation,
                                    disabled: !this.state.dataSlots.transformation,
                                    icon: OEIconCodes.arrowPasteTransformation
                                }
                            ]}
                            onPressed={this.onDataSlotButtonPressed}
                        />

                        <OEControl>
                            <OEObjectModelValueEdit
                                model={OEArrowModels.transformation}
                                object={this.state.data ? this.state.data.transform : null}
                                onChange={this.onTransformationChange}
                            />
                        </OEControl>

                    </OEGroupControl>

                </div>
            </React.Fragment>
        );
    }

    onStyleChange(sender, params) {
        if(!this.oe.isReady() || this.id === null || this.data === null) return;

        let style = Object.assign({}, this.data.style, params);

        this.oe.sharedInterface.getUIControllerArrow().setArrowStyle(this.id, style);
    }

    onTransformationChange(sender, params)  {
        if(!this.oe.isReady() || this.id === null) return;

        this.oe.sharedInterface.getUIControllerArrow().setArrowTransform(this.id, {...params});
    }

    onDataSlotButtonPressed(sender, id)  {
        if(!this.oe.isReady() || this.id === null) return;
        let controller = this.oe.sharedInterface.getUIControllerArrow();

        if(id === OEArrowExtControllerBtnIds.copyStyle)    {
            controller.fillArrowDataSlot(OEArrowDataSlots.style, this.id);
        } else if(id === OEArrowExtControllerBtnIds.pasteStyle) {
            controller.applyArrowDataSlot(OEArrowDataSlots.style, this.id, OEArrowDataFlags.style);
        } else if(id === OEArrowExtControllerBtnIds.copyTransformation)    {
            controller.fillArrowDataSlot(OEArrowDataSlots.transformation, this.id);
        } else if(id === OEArrowExtControllerBtnIds.pasteTransformation) {
            controller.applyArrowDataSlot(OEArrowDataSlots.transformation, this.id, OEArrowDataFlags.transform);
        }
    }
}

OEArrowExtController.defaultProps = {
    moduleId: '',
    className: ''
};

OEArrowExtController.propTypes = {
    moduleId: PropTypes.string,
    className: PropTypes.string,
    id: PropTypes.number
}; 

export const OEArrowExtPopoverController = OEPopover.coat(OEArrowExtController, {
    noHeader: true,
}, {
    placement: 'right',
    buttonClassName: 'transparent-btn',
    controllerClassName: 'arrow-ext-popover compact'
}, {
    controllerClassName: PropTypes.string
});